<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { handleAxiosError } from '@/composables/useAxiosError'
import { fetchPendingTerms } from '@/composables/useTerms'
import { formatVersionDate } from '@/legal/termsBundle'
import type { TermsPendingList } from '@/types'

defineOptions({ name: 'TermsPendingList' })

const { t } = useI18n()

const loading = ref(true)
const failed = ref(false)
const list = ref<TermsPendingList | null>(null)
const searchQuery = ref('')

const fetchList = async () => {
  loading.value = true
  failed.value = false
  try {
    list.value = await fetchPendingTerms()
    failed.value = list.value === null
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'TermsPendingList' })
    failed.value = true
  } finally {
    loading.value = false
  }
}

// Server order (oldest notice first, never notified last) is kept.
const rows = computed(() => {
  const accounts = list.value?.accounts ?? []
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return accounts
  return accounts.filter(
    (a) => a.email.toLowerCase().includes(query) || a.name.toLowerCase().includes(query) || String(a.account_id) === query
  )
})

const versionLabel = (id: string | null) =>
  id ? formatVersionDate(id, 'de') : t('dashboard.admin.compliance.termsPending.beforeFirstVersion')
const formatDate = (iso: string | null) => (iso ? DateTime.fromISO(iso).toFormat('dd.MM.yyyy') : null)

onMounted(fetchList)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.compliance.termsPending.title')"
    :subtitle="t('dashboard.admin.compliance.termsPending.subtitle')"
    icon="mdi-file-document-alert-outline"
    icon-color="amber"
  >
    <template #actions>
      <button type="button" class="dashboard-action-btn" :disabled="loading" @click="fetchList">
        <v-icon icon="mdi-refresh" size="18" />
        <span>{{ t('dashboard.admin.compliance.termsPending.refresh') }}</span>
      </button>
    </template>

    <div v-if="loading" class="dashboard-loading-state">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <p v-else-if="failed" class="dashboard-alert dashboard-alert--error" data-test="terms-pending-error">
      {{ t('dashboard.admin.compliance.termsPending.fetchError') }}
    </p>

    <section v-else-if="list" class="section-card">
      <div class="section-header">
        <p class="measure" data-test="terms-pending-measure">
          <template v-if="list.material_version">
            {{ t('dashboard.admin.compliance.termsPending.measuredAgainst', { date: versionLabel(list.material_version) }) }}
          </template>
          <template v-else>{{ t('dashboard.admin.compliance.termsPending.noMaterialVersion') }}</template>
        </p>
        <div class="dashboard-search-wrapper">
          <v-icon icon="mdi-magnify" size="18" class="dashboard-search-icon" />
          <input
            v-model="searchQuery"
            type="search"
            class="dashboard-search-input"
            :placeholder="t('dashboard.admin.compliance.termsPending.searchPlaceholder')"
          />
        </div>
      </div>

      <p class="manual-note">{{ t('dashboard.admin.compliance.termsPending.manualNote') }}</p>

      <div v-if="rows.length === 0" class="dashboard-empty-state" data-test="terms-pending-empty">
        <p class="dashboard-empty-title">{{ t('dashboard.admin.compliance.termsPending.empty') }}</p>
      </div>

      <div v-else class="data-table">
        <div class="table-header">
          <span>{{ t('dashboard.admin.compliance.termsPending.columns.account') }}</span>
          <span>{{ t('dashboard.admin.compliance.termsPending.columns.governing') }}</span>
          <span>{{ t('dashboard.admin.compliance.termsPending.columns.accepted') }}</span>
          <span>{{ t('dashboard.admin.compliance.termsPending.columns.notified') }}</span>
          <span class="num">{{ t('dashboard.admin.compliance.termsPending.columns.days') }}</span>
          <span class="num">{{ t('dashboard.admin.compliance.termsPending.columns.contracts') }}</span>
        </div>
        <div v-for="row in rows" :key="row.account_id" class="table-row" data-test="terms-pending-row">
          <span class="account">
            <router-link :to="{ name: 'dashboardAdminUserDetail', params: { id: row.account_id } }" class="account-link">
              {{ row.name || row.email }}
            </router-link>
            <span class="account-sub">#{{ row.account_id }} · {{ row.email }}</span>
          </span>
          <span>{{ versionLabel(row.governing_version) }}</span>
          <span>
            <template v-if="row.accepted_version">
              {{ formatVersionDate(row.accepted_version, 'de') }}
              <span class="account-sub">{{ formatDate(row.accepted_at) }}</span>
            </template>
            <template v-else>{{ t('dashboard.admin.compliance.termsPending.none') }}</template>
          </span>
          <span :class="{ muted: !row.notice_sent_at }">
            {{ formatDate(row.notice_sent_at) ?? t('dashboard.admin.compliance.termsPending.notNotified') }}
          </span>
          <span class="num">{{ row.days_since_notice ?? '' }}</span>
          <span class="num">{{ row.pending_contracts }}</span>
        </div>
      </div>
    </section>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.measure,
.manual-note {
  margin: 0;
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.7);
}

.manual-note {
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.data-table {
  overflow-x: auto;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: minmax(220px, 2fr) 1fr 1fr 1fr 90px 90px;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  min-width: 760px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }
}

.num {
  text-align: right;
}

.account {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-link {
  color: #4dd0e1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.account-sub {
  display: block;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  overflow-wrap: anywhere;
}

.muted {
  color: rgba(200, 218, 255, 0.5);
}

@media (max-width: 600px) {
  .section-card {
    padding: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .table-row {
    transition: none;
  }
}
</style>
